import { Deserializer } from 'jsonapi-serializer'

const isJsonApiResource = (value) => {
  return Boolean(
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    value.attributes &&
    typeof value.attributes === 'object' &&
    value.id !== undefined &&
    value.type
  )
}

const deepDeserializeNestedResources = (value) => {
  if (Array.isArray(value)) {
    return value.map(deepDeserializeNestedResources)
  }

  if (!value || typeof value !== 'object') {
    return value
  }

  if (isJsonApiResource(value)) {
    return {
      id: value.id,
      ...deepDeserializeNestedResources(value.attributes)
    }
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, nestedValue]) => [
      key,
      deepDeserializeNestedResources(nestedValue)
    ])
  )
}

/**
 * Deserializes JSON:API formatted responses to plain JavaScript objects
 * Converts attribute keys from snake_case/kebab-case to camelCase
 *
 * @param {Object} response - The raw API response
 * @param {Object} [response.data] - The JSON:API data object
 * @returns {Promise<Object|Array|null>} Deserialized data or original response if no data
 *
 * @example
 * const response = {
 *   data: {
 *     type: 'users',
 *     id: '1',
 *     attributes: { display_name: 'John Doe' }
 *   }
 * }
 * const result = await deserializeResponse(response)
 * // Returns: { id: '1', displayName: 'John Doe' }
 */
export const deserializeResponse = async (response) => {
  if (!response || !response.data) {
    return response
  }

  const deserializer = new Deserializer({
    keyForAttribute: 'camelCase'
  })

  const deserialized = await deserializer.deserialize(response)
  return deepDeserializeNestedResources(deserialized)
}
